import { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAvailableUnits, createLead } from '@/lib/api';
import { toast } from 'sonner';
import { UnitOverridePicker } from './UnitOverridePicker';
import { StartEndDateFields } from '@/components/common/DatePickers';

function StepChip({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 mr-1.5 mb-1">
      {label}
    </span>
  );
}

export function LeadCreatePanel({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [desiredStartDate, setDesiredStartDate] = useState('');
  const [desiredEndDate, setDesiredEndDate] = useState('');
  const [availableUnits, setAvailableUnits] = useState([]);
  const [potentialUnitIds, setPotentialUnitIds] = useState([]);
  const [isUnassigned, setIsUnassigned] = useState(false);
  const [source, setSource] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('');
  const [leadStrength, setLeadStrength] = useState(1);
  const [priceOffered, setPriceOffered] = useState('');
  const [pets, setPets] = useState('');
  const [parkingRequest, setParkingRequest] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { if (open) resetForm(); }, [open]);

  const resetForm = () => {
    setStep(1);
    setName(''); setDesiredStartDate(''); setDesiredEndDate('');
    setAvailableUnits([]); setPotentialUnitIds([]); setIsUnassigned(false);
    setSource(''); setPhone(''); setEmail(''); setPreferredContactMethod('');
    setLeadStrength(1); setPriceOffered('');
    setPets(''); setParkingRequest(''); setNotes('');
    setSaving(false); setShowDiscard(false);
  };

  const handleClose = () => {
    if (step > 1) setShowDiscard(true);
    else onClose();
  };

  // Fetch available units when dates change in step 2+
  const fetchUnits = useCallback(async () => {
    if (desiredStartDate && desiredEndDate) {
      try {
        const units = await getAvailableUnits(desiredStartDate, desiredEndDate);
        setAvailableUnits(units);
      } catch { setAvailableUnits([]); }
    } else {
      setAvailableUnits([]);
    }
  }, [desiredStartDate, desiredEndDate]);

  useEffect(() => { if (step >= 3) fetchUnits(); }, [fetchUnits, step]);

  const toggleUnit = (unitId) => {
    setPotentialUnitIds(prev =>
      prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await createLead({
        name: name.trim(), source, phone, email,
        desired_start_date: desiredStartDate, desired_end_date: desiredEndDate,
        potential_unit_ids: isUnassigned ? [] : potentialUnitIds,
        pets, parking_request: parkingRequest,
        lead_strength: leadStrength,
        price_offered: priceOffered ? parseFloat(priceOffered) : null,
        preferred_contact_method: preferredContactMethod,
        notes, is_unassigned: isUnassigned,
      });
      toast.success('Lead created');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally { setSaving(false); }
  };

  const summaryChips = [];
  if (name) summaryChips.push(name);
  if (desiredStartDate && desiredEndDate && step > 2) summaryChips.push(`${desiredStartDate} — ${desiredEndDate}`);

  if (!open) return null;

  const noUnitsForDates = desiredStartDate && desiredEndDate && availableUnits.length === 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-full bg-white z-50 shadow-xl flex flex-col"
        style={{ borderLeft: '0.5px solid #e5e7eb', animation: 'slideInRight 200ms ease-out' }}
        data-testid="lead-create-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-[15px] font-semibold text-gray-900">New Lead</h2>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-gray-100" data-testid="lead-create-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {showDiscard && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-[13px] text-amber-700">Discard changes?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDiscard(false)}>Keep editing</Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { resetForm(); onClose(); }}>Discard</Button>
            </div>
          </div>
        )}

        {summaryChips.length > 0 && (
          <div className="px-5 pt-3 pb-1 flex flex-wrap">
            {summaryChips.map((c, i) => <StepChip key={i} label={c} />)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* STEP 1: Lead Name */}
          <div>
            <p className="text-[13px] font-semibold text-gray-700 mb-3">1. Lead name</p>
            <div>
              <Label className="text-[12px] text-gray-500">Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Lead name" className="h-9 text-[13px] mt-1" data-testid="lead-create-name" />
            </div>
            {step === 1 && (
              <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => {
                if (!name.trim()) { toast.error('Name is required'); return; }
                setStep(2);
              }} data-testid="lead-step1-next">
                Next <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>

          {/* STEP 2: Desired Dates */}
          {step >= 2 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">2. Dates desired</p>
              <div className="space-y-3">
                <StartEndDateFields
                  start={desiredStartDate}
                  end={desiredEndDate}
                  onChange={({ start, end }) => { setDesiredStartDate(start); setDesiredEndDate(end); }}
                  startTestId="lead-create-start-date"
                  endTestId="lead-create-end-date"
                />
              </div>
              {step === 2 && (
                <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => setStep(3)} data-testid="lead-step2-next">
                  Next <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {/* STEP 3: Property Interest (Available Units) */}
          {step >= 3 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">3. Property interest</p>
              {noUnitsForDates && !isUnassigned ? (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[12px] font-medium text-amber-900">No units available for these dates</p>
                      <p className="text-[11px] text-amber-700 mt-0.5">Save as unassigned to follow up later.</p>
                      <Button variant="outline" size="sm" className="mt-2 h-6 text-[11px] border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => setIsUnassigned(true)} data-testid="lead-create-mark-unassigned">
                        Mark as Unassigned
                      </Button>
                    </div>
                  </div>
                </div>
              ) : isUnassigned ? (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <span className="text-[12px] font-medium text-amber-900">Saving as unassigned</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => setIsUnassigned(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 p-3 rounded-lg border min-h-[40px]">
                  {availableUnits.length === 0 ? (
                    <span className="text-[12px] text-muted-foreground">
                      {desiredStartDate && desiredEndDate ? 'Loading...' : 'Select dates to see available units'}
                    </span>
                  ) : (
                    availableUnits.map(u => (
                      <Badge key={u.id}
                        variant={potentialUnitIds.includes(u.id) ? 'default' : 'outline'}
                        className="cursor-pointer text-[11px]"
                        onClick={() => toggleUnit(u.id)}
                        data-testid={`lead-create-unit-${u.id}`}>
                        {u.property_name} - {u.unit_number}
                      </Badge>
                    ))
                  )}
                </div>
              )}
              {!isUnassigned && (
                <div className="mt-3">
                  <UnitOverridePicker
                    selectedIds={potentialUnitIds}
                    availableIds={availableUnits.map(u => u.id)}
                    onToggle={toggleUnit}
                    startDate={desiredStartDate}
                    endDate={desiredEndDate}
                  />
                </div>
              )}
              {step === 3 && (
                <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => setStep(4)} data-testid="lead-step3-next">
                  Next <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {/* STEP 4: Source + Contact */}
          {step >= 4 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">4. Source & contact</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Source</Label>
                  <Input value={source} onChange={e => setSource(e.target.value)} placeholder="Where was lead found" className="h-9 text-[13px] mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[12px] text-gray-500">Phone</Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-gray-500">Email</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Preferred Contact Method</Label>
                  <Input value={preferredContactMethod} onChange={e => setPreferredContactMethod(e.target.value)} placeholder="e.g. Text, Email, Call" className="h-9 text-[13px] mt-1" />
                </div>
              </div>
              {step === 4 && (
                <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => setStep(5)} data-testid="lead-step4-next">
                  Next <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {/* STEP 5: Status & extras */}
          {step >= 5 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">5. Status & details</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[12px] text-gray-500">Lead Strength</Label>
                    <Select value={String(leadStrength)} onValueChange={v => setLeadStrength(parseInt(v))}>
                      <SelectTrigger className="h-9 text-[13px] mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Weak</SelectItem>
                        <SelectItem value="2">2 - Fair</SelectItem>
                        <SelectItem value="3">3 - Good</SelectItem>
                        <SelectItem value="4">4 - Strong</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Price Offered</Label>
                  <Input type="number" value={priceOffered} onChange={e => setPriceOffered(e.target.value)} placeholder="$ amount" className="h-9 text-[13px] mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[12px] text-gray-500">Pets</Label>
                    <Input value={pets} onChange={e => setPets(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-gray-500">Parking Request</Label>
                    <Input value={parkingRequest} onChange={e => setParkingRequest(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Notes</Label>
                  <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." className="text-[13px] mt-1" rows={3} data-testid="lead-create-notes" />
                </div>
              </div>
            </div>
          )}
        </div>

        {step >= 5 && (
          <div className="px-5 py-3 border-t bg-gray-50">
            <Button className="w-full h-9 text-[13px]" disabled={!name.trim() || saving} onClick={handleCreate} data-testid="lead-create-submit">
              {saving ? 'Creating...' : (isUnassigned ? 'Save as unassigned' : 'Create lead')}
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
